import React from "react";
import { Link } from "react-router-dom";
import Reveal from "../Components/Reveal";

const membershipOptions = [
  {
    title: "TBA Listed Team",
    description:
      "Security and incident response teams can register as listed teams within the TrustBroker Africa registry.",
    detail:
      "Listed teams become visible within the community directory and can collaborate with other participating teams across Africa.",
    imgSrc: "/assets/images/listed.jpeg",
    alt: "TrustBroker Africa listed team",
    link: "/join/listed-team",
  },
  {
    title: "TBA Accredited Team",
    description:
      "Accredited teams demonstrate a higher level of operational maturity and commitment to the TrustBroker Africa trust framework.",
    detail:
      "Accreditation recognises teams that meet defined operational and community participation standards.",
    imgSrc: "/assets/images/accredited.jpeg",
    alt: "TrustBroker Africa accredited team",
    link: "/join/accredited-team",
  },
  {
    title: "TBA Associated Expert",
    description:
      "Cybersecurity professionals, researchers, and practitioners can join the TrustBroker Africa community as associated experts.",
    detail:
      "Associated experts contribute knowledge, participate in community discussions, and support collaboration activities.",
    imgSrc: "/assets/images/associated.jpeg",
    alt: "TrustBroker Africa associated expert",
    link: "/join/associated-team",
  },
];

const participantBenefits = [
  "Collaboration with incident response teams across Africa",
  "Access to trusted professional networks",
  "Participation in training workshops and technical exchanges",
  "Opportunities to participate in cyber defence exercises",
  "Engagement with the broader cybersecurity community",
];

const shell = "rounded-[28px] bg-white px-6 py-8 shadow-panel md:px-8 md:py-10";

function Join() {
  return (
    <div className="space-y-8 text-ink-900">
      <Reveal as="section" className={shell}>
        <div className="space-y-6">
          <h1 className="m-0 text-4xl text-left font-semibold tracking-tight text-sky-700 md:text-5xl">
            Join TrustBroker Africa
          </h1>
          <div className="text-lg leading-8 text-slate-700">
            <p className="m-0">
              TrustBroker Africa is a pan-African community connecting security
              and incident response teams working to protect digital
              infrastructure across the continent.
            </p>
            <p className="m-0">
              The community brings together CSIRTs, SOC teams, researchers, and
              cybersecurity professionals to strengthen collaboration, improve
              operational capabilities, and share knowledge across Africa&apos;s
              cybersecurity ecosystem.
            </p>
            <p className="m-0">
              Teams and individuals can participate in TrustBroker Africa
              through several levels of engagement depending on their role and
              level of operational maturity.
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal as="section" className={shell} delay={120}>
        <div className="grid gap-8 lg:grid-cols-1">
          <div className="space-y-6">
            <h2 className="m-0 text-3xl font-semibold tracking-tight text-sky-700 md:text-4xl">
              Why Join TrustBroker Africa
            </h2>
            <p className="m-0 text-lg leading-8 text-slate-700">
              Joining TrustBroker Africa provides organisations and
              cybersecurity professionals with opportunities to collaborate with
              peers across Africa, strengthen operational capabilities, and
              contribute to improving cybersecurity resilience across the
              continent.
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="m-0 text-xl font-semibold text-ink-900">
              Participants benefit from:
            </h3>
            <ul className="m-0 grid list-none gap-4 p-0 text-left sm:grid-cols-2">
              {participantBenefits.map((benefit) => (
                <li
                  key={benefit}
                  className="flex gap-3 rounded-lg border border-sky-100 bg-sky-50/60 p-4 text-base leading-7 text-slate-700"
                >
                  <span className="mt-2 h-2.5 w-2.5 flex-none rounded-full bg-sky-700" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>

      <Reveal as="section" className={shell} delay={220}>
        <div className="space-y-6">
          <div className="space-y-3 text-left">
            <h2 className="m-0 text-3xl font-semibold tracking-tight text-sky-700 md:text-4xl">
              Participation Pathways
            </h2>
          </div>
          <div className="grid gap-5 lg:grid-cols-3">
            {membershipOptions.map((option, index) => (
              <Reveal
                as="article"
                key={option.title}
                className="group flex h-full flex-col overflow-hidden rounded-[24px] bg-slate-50 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-float"
                delay={index * 90 + 240}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={option.imgSrc}
                    alt={option.alt}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-4 px-6 py-6 text-left">
                  <h3 className="m-0 text-2xl font-semibold leading-tight text-ink-900">
                    {option.title}
                  </h3>
                  <p className="m-0 text-base leading-7 text-slate-700">
                    {option.description}
                  </p>
                  <p className="m-0 text-base leading-7 text-slate-700">
                    {option.detail}
                  </p>
                  <div className="pt-2">
                    <Link
                      to={option.link}
                      className="inline-flex items-center rounded-full bg-sky-700 px-5 py-3 text-sm font-semibold text-white no-underline transition duration-300 hover:-translate-y-0.5 hover:bg-sky-800 hover:text-white"
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  );
}

export default Join;
